import { Controller } from "@hotwired/stimulus"
import { debounce } from "lodash-es"

// Connects to data-controller="autosave"
// Usage:
// <div data-controller="autosave" data-autosave-delay-value="1500" data-autosave-enabled-value="true">
//   <form data-action="input->autosave#save change->autosave#save">
//     ...
//   </form>
// </div>
export default class extends Controller {
  static values = { delay: Number, enabled: Boolean }
  static targets = ["status", "toggle", "notice"]

  connect() {
    // Try to find form in parent elements first
    this.form = this.element.closest("form")
    // If not found, try to find form within this element
    if (!this.form) {
      this.form = this.element.querySelector("form")
    }
    
    if (!this.form) {
      console.error("Autosave controller: No form found")
      return
    }

    this.url = this.form.action || this.form.getAttribute('action')
    
    // Wenn keine URL vorhanden ist, versuche sie aus dem Model zu konstruieren
    if (!this.url || this.url === '') {
      console.warn("Autosave controller: No form action found, autosave disabled")
      this.enabled = false
      return
    }
    
    this.delay = this.delayValue || 1500
    
    // Load preference from localStorage
    if (typeof Storage !== "undefined") {
      const saved = localStorage.getItem("autosave_enabled")
      this.enabled = saved !== null ? saved === "true" : (this.enabledValue !== false)
    } else {
      this.enabled = this.enabledValue !== false
    }
    
    // Update enabledValue to match loaded preference
    this.enabledValue = this.enabled

    // Create debounced save function
    this.debouncedSave = debounce(() => {
      this.performSave()
    }, this.delay)

    // Show initial status - use setTimeout to ensure DOM is ready
    setTimeout(() => {
      this.updateStatus("ready")
      this.updateToggle()
      this.updateNotice()
    }, 0)
  }

  toggle() {
    this.enabled = !this.enabled
    this.enabledValue = this.enabled
    this.updateToggle()
    this.updateNotice()
    
    // Save preference to localStorage
    if (typeof Storage !== "undefined") {
      localStorage.setItem("autosave_enabled", this.enabled.toString())
    }
  }

  updateToggle() {
    if (this.hasToggleTarget) {
      this.toggleTarget.checked = this.enabled
      // Update visual toggle state
      const toggleSwitch = this.toggleTarget.closest('label')?.querySelector('.toggle-switch')
      if (toggleSwitch) {
        if (this.enabled) {
          toggleSwitch.classList.remove('bg-gray-200')
          toggleSwitch.classList.add('bg-blue-600')
          const toggleDot = toggleSwitch.querySelector('.toggle-dot')
          if (toggleDot) {
            toggleDot.classList.remove('translate-x-0')
            toggleDot.classList.add('translate-x-5')
          }
        } else {
          toggleSwitch.classList.remove('bg-blue-600')
          toggleSwitch.classList.add('bg-gray-200')
          const toggleDot = toggleSwitch.querySelector('.toggle-dot')
          if (toggleDot) {
            toggleDot.classList.remove('translate-x-5')
            toggleDot.classList.add('translate-x-0')
          }
        }
      }
    }
  }

  updateNotice() {
    // Update notice visibility and text based on enabled state
    // The notice might be outside the form, so search in document
    let notice = null
    if (this.hasNoticeTarget) {
      notice = this.noticeTarget
    } else {
      // Fallback: search in parent container
      const container = this.element.closest('.inner, .content, .block')
      if (container) {
        notice = container.querySelector('[data-autosave-target="notice"]')
      }
    }
    
    if (notice) {
      if (this.enabled) {
        notice.classList.remove("bg-gray-50", "border-gray-200")
        notice.classList.add("bg-blue-50", "border-blue-200")
        const title = notice.querySelector("span.text-sm.font-medium")
        const description = notice.querySelector("span.text-xs")
        if (title) title.textContent = "Automatisches Speichern aktiviert"
        if (description) description.textContent = "Änderungen werden automatisch nach 1,5 Sekunden gespeichert"
      } else {
        notice.classList.remove("bg-blue-50", "border-blue-200")
        notice.classList.add("bg-gray-50", "border-gray-200")
        const title = notice.querySelector("span.text-sm.font-medium")
        const description = notice.querySelector("span.text-xs")
        if (title) title.textContent = "Automatisches Speichern deaktiviert"
        if (description) description.textContent = "Änderungen werden nicht automatisch gespeichert"
      }
    }
  }

  save() {
    if (!this.enabled) return
    
    this.updateStatus("saving")
    this.debouncedSave()
  }

  enable() {
    this.enabled = true
    this.enabledValue = true
    this.updateToggle()
    this.updateNotice()
    
    // Save preference to localStorage
    if (typeof Storage !== "undefined") {
      localStorage.setItem("autosave_enabled", "true")
    }
  }

  disable() {
    this.enabled = false
    this.enabledValue = false
    this.updateToggle()
    this.updateNotice()
    
    // Save preference to localStorage
    if (typeof Storage !== "undefined") {
      localStorage.setItem("autosave_enabled", "false")
    }
  }

  performSave() {
    if (!this.form) return

    const formData = new FormData(this.form)
    
    // Add method override for PATCH/PUT
    const method = formData.get("_method") || this.form.method || "POST"
    if (method !== "POST") {
      formData.set("_method", method)
    }

    fetch(this.url, {
      method: "POST",
      body: formData,
      headers: {
        "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")?.content || "",
        "Accept": "text/vnd.turbo-stream.html, text/html, application/xhtml+xml",
      },
      credentials: "same-origin",
    })
    .then((response) => {
      if (response.ok) {
        return response.text()
      } else {
        throw new Error(`HTTP error! status: ${response.status}`)
      }
    })
    .then((html) => {
      // Process Turbo Stream response
      if (html.includes("turbo-stream")) {
        Turbo.renderStreamMessage(html)
      }
      this.updateStatus("saved")
      
      // Reset status after 2 seconds
      setTimeout(() => {
        this.updateStatus("ready")
      }, 2000)
    })
    .catch((error) => {
      console.error("Autosave error:", error)
      this.updateStatus("error")
      
      // Reset status after 3 seconds
      setTimeout(() => {
        this.updateStatus("ready")
      }, 3000)
    })
  }

  updateStatus(status) {
    if (this.hasStatusTarget) {
      const statusElement = this.statusTarget
      statusElement.textContent = this.getStatusText(status)
      statusElement.className = this.getStatusClass(status)
    }
  }

  getStatusText(status) {
    const texts = {
      ready: "",
      saving: "Speichere...",
      saved: "✓ Gespeichert",
      error: "✗ Fehler beim Speichern"
    }
    return texts[status] || ""
  }

  getStatusClass(status) {
    const classes = {
      ready: "text-sm text-gray-500",
      saving: "text-sm text-blue-500",
      saved: "text-sm text-green-500",
      error: "text-sm text-red-500"
    }
    return classes[status] || "text-sm text-gray-500"
  }
}

